<template>
  <div class="qb-widget">
    <!-- Trigger pill (shown when closed) -->
    <transition name="qb-trigger-fade">
      <button v-if="!chatOpen" class="qb-trigger-btn" @click="chatOpen = true">
        <img :src="isBlinking ? qualibotBlink : qualibotOpen" class="qb-trigger-icon" alt="Qualibot" />
        <span>Ask Qualibot</span>
      </button>
    </transition>

    <!-- Floating chat panel -->
    <transition name="qb-panel-slide">
      <div v-if="chatOpen" class="qb-panel">

        <!-- Header -->
        <div class="qb-header">
          <div class="qb-header-left">
            <div class="qb-header-title">Qualibot</div>
            <div class="qb-header-sub">Ask me anything about the platform</div>
          </div>
          <button class="qb-close" @click="chatOpen = false">✕</button>
        </div>

        <!-- Messages -->
        <div class="qb-messages" ref="qbMessages">
          <!-- Welcome -->
          <div v-if="chatMessages.length === 0" class="qb-welcome">
            <div class="qb-welcome-icon">
              <img :src="isBlinking ? qualibotBlink : qualibotOpen" class="qb-welcome-img" alt="Qualibot" />
            </div>
            <div class="qb-welcome-name">Qualibot</div>
            <p>Hi! I'm your Quali-D guide assistant.</p>
            <p>Ask me anything — in English or Arabic.</p>
          </div>

          <!-- Messages -->
          <div
            v-for="(msg, i) in chatMessages"
            :key="i"
            class="qb-msg"
            :class="msg.role === 'user' ? 'qb-msg--user' : 'qb-msg--ai'"
          >
            <div v-if="msg.role === 'assistant'" class="qb-avatar-wrap">
              <img :src="isBlinking ? qualibotBlink : qualibotOpen" class="qb-avatar-img" alt="Qualibot" />
            </div>
            <div
              class="qb-bubble"
              :class="{ 'qb-bubble--rtl': isRTL(msg.content) }"
              v-html="renderMarkdown(msg.content)"
            ></div>
          </div>

          <!-- Typing indicator -->
          <div v-if="chatLoading" class="qb-msg qb-msg--ai">
            <div class="qb-avatar-wrap">
              <img :src="isBlinking ? qualibotBlink : qualibotOpen" class="qb-avatar-img" alt="Qualibot" />
            </div>
            <div class="qb-bubble qb-typing">
              <span></span><span></span><span></span>
            </div>
          </div>
        </div>

        <!-- Input -->
        <div class="qb-input-row">
          <textarea
            ref="qbInput"
            v-model="chatInput"
            class="qb-input"
            placeholder="Type your question…"
            rows="1"
            :disabled="chatLoading"
            @keydown.enter.exact.prevent="sendMessage"
            @keydown.enter.shift.exact="() => {}"
            @input="autoResize"
          ></textarea>
          <button class="qb-send" :disabled="chatLoading || !chatInput.trim()" @click="sendMessage">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor"><path d="M2.01 21L23 12 2.01 3 2 10l15 2-15 2z"/></svg>
          </button>
        </div>

      </div>
    </transition>
  </div>
</template>

<script>
import qualibotOpen from '@/assets/qualibot_eye_open.png'
import qualibotBlink from '@/assets/qualibot_eye_blink.png'

export default {
  name: 'QualibotChat',

  data () {
    return {
      qualibotOpen,
      qualibotBlink,
      isBlinking: false,
      blinkTimer: null,
      blinkScheduleTimer: null,
      chatOpen: false,
      chatMessages: [],
      chatInput: '',
      chatLoading: false
    }
  },

  mounted () {
    this.scheduleNextBlink()
  },

  beforeUnmount () {
    clearTimeout(this.blinkTimer)
    clearTimeout(this.blinkScheduleTimer)
  },

  methods: {
    scheduleNextBlink () {
      const gap = 2000 + Math.random() * 3000
      this.blinkScheduleTimer = setTimeout(() => {
        this.isBlinking = true
        this.blinkTimer = setTimeout(() => {
          this.isBlinking = false
          this.scheduleNextBlink()
        }, 140)
      }, gap)
    },

    async sendMessage () {
      const text = this.chatInput.trim()
      if (!text || this.chatLoading) return

      this.chatMessages.push({ role: 'user', content: text })
      this.chatInput = ''
      this.resetResize()
      this.chatLoading = true
      this.scrollChat()

      const history = this.chatMessages.slice(-11, -1)

      try {
        const res = await fetch('https://api.quali-d.com/ai-guide-chat', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ message: text, history })
        })
        const data = await res.json()
        if (data.reply) {
          this.chatMessages.push({ role: 'assistant', content: data.reply })
        } else {
          this.chatMessages.push({ role: 'assistant', content: data.error || 'Something went wrong. Please try again.' })
        }
      } catch (e) {
        this.chatMessages.push({ role: 'assistant', content: 'Could not reach the AI service. Check your connection.' })
      } finally {
        this.chatLoading = false
        this.scrollChat()
        this.$nextTick(() => this.$refs.qbInput && this.$refs.qbInput.focus())
      }
    },

    scrollChat () {
      this.$nextTick(() => {
        const el = this.$refs.qbMessages
        if (el) el.scrollTop = el.scrollHeight
      })
    },

    isRTL (text) {
      return /[\u0600-\u06FF]/.test((text || '').slice(0, 80))
    },

    renderMarkdown (text) {
      if (!text) return ''
      const html = text
        .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
        .replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')
        .replace(/(?<!\*)\*(?!\*)(.*?)(?<!\*)\*(?!\*)/g, '<em>$1</em>')
        .replace(/^###\s+(.+)$/gm, '<div class="qb-md-h">$1</div>')
        .replace(/^(\d+)\.\s+(.+)$/gm, '<div class="qb-md-step"><span class="qb-md-num">$1</span><span>$2</span></div>')
        .replace(/^[-•]\s+(.+)$/gm, '<div class="qb-md-bullet"><span class="qb-md-dot">●</span><span>$1</span></div>')
        .replace(/\n{2,}/g, '<div class="qb-md-gap"></div>')
        .replace(/\n/g, '<br>')
      return html
    },

    autoResize () {
      const el = this.$refs.qbInput
      if (!el) return
      el.style.height = 'auto'
      el.style.height = Math.min(el.scrollHeight, 120) + 'px'
    },

    resetResize () {
      const el = this.$refs.qbInput
      if (!el) return
      el.style.height = 'auto'
    }
  }
}
</script>

<style scoped>
/* ── Widget wrapper (fixed bottom-right) ── */
.qb-widget {
  position: fixed;
  bottom: 24px;
  right: 24px;
  z-index: 9999;
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 12px;
}

/* ── Trigger pill button ── */
.qb-trigger-btn {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 6px 16px 6px 8px;
  font-size: .78rem;
  font-weight: 700;
  color: #fff;
  background: linear-gradient(135deg, #667eea, #764ba2);
  border: none;
  border-radius: 999px;
  cursor: pointer;
  font-family: inherit;
  transition: all .2s;
  white-space: nowrap;
  box-shadow: 0 2px 12px rgba(102,126,234,.45);
  letter-spacing: .02em;
}
.qb-trigger-btn:hover {
  box-shadow: 0 4px 18px rgba(102,126,234,.6);
  transform: translateY(-1px);
}
@keyframes qb-bob {
  0%, 100% { transform: translateY(0); }
  50%       { transform: translateY(-2px); }
}
.qb-trigger-icon {
  width: 30px; height: 30px;
  object-fit: contain;
  animation: qb-bob 3s ease-in-out infinite;
  filter: drop-shadow(0 1px 5px rgba(255,255,255,.4));
  flex-shrink: 0;
}

/* ── Transitions ── */
.qb-trigger-fade-enter-active,
.qb-trigger-fade-leave-active { transition: opacity .15s, transform .15s; }
.qb-trigger-fade-enter-from,
.qb-trigger-fade-leave-to  { opacity: 0; transform: scale(.9); }

.qb-panel-slide-enter-active,
.qb-panel-slide-leave-active { transition: all .25s cubic-bezier(.4,0,.2,1); }
.qb-panel-slide-enter-from,
.qb-panel-slide-leave-to { opacity: 0; transform: translateY(20px) scale(.96); }

/* ── Chat panel ── */
.qb-panel {
  width: 360px;
  max-height: 520px;
  background: #fff;
  border-radius: 18px;
  box-shadow: 0 8px 40px rgba(0,0,0,.18);
  display: flex;
  flex-direction: column;
  overflow: hidden;
  border: 1px solid #e2e8f0;
}

/* Header */
.qb-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 14px 16px;
  background: linear-gradient(135deg, #667eea, #764ba2);
  color: #fff;
  flex-shrink: 0;
}
.qb-header-left { display: flex; flex-direction: column; gap: 2px; }
.qb-header-title { font-size: .88rem; font-weight: 800; }
.qb-header-sub   { font-size: .7rem; opacity: .8; }
.qb-close {
  background: rgba(255,255,255,.15);
  border: none;
  color: #fff;
  width: 28px; height: 28px;
  border-radius: 50%;
  cursor: pointer;
  font-size: .8rem;
  display: flex; align-items: center; justify-content: center;
  transition: background .15s;
  flex-shrink: 0;
}
.qb-close:hover { background: rgba(255,255,255,.3); }

/* Messages area */
.qb-messages {
  flex: 1;
  overflow-y: auto;
  padding: 16px;
  display: flex;
  flex-direction: column;
  gap: 10px;
  background: #f8fafc;
  scroll-behavior: smooth;
}
.qb-messages::-webkit-scrollbar { width: 4px; }
.qb-messages::-webkit-scrollbar-thumb { background: #cbd5e0; border-radius: 4px; }

/* Welcome state */
.qb-welcome {
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  padding: 20px 10px;
  color: #718096;
  font-size: .84rem;
  line-height: 1.6;
}
.qb-welcome-icon { margin-bottom: 6px; }
.qb-welcome-img {
  width: 80px; height: 80px;
  object-fit: contain;
  animation: qb-bob 3s ease-in-out infinite;
  filter: drop-shadow(0 4px 14px rgba(99,102,241,.3));
}
.qb-welcome-name {
  font-size: .75rem;
  font-weight: 800;
  color: #667eea;
  letter-spacing: .07em;
  margin-bottom: 8px;
}

/* Message rows */
.qb-msg { display: flex; align-items: flex-end; gap: 6px; }
.qb-msg--user { justify-content: flex-end; }
.qb-msg--ai   { justify-content: flex-start; }

.qb-avatar-wrap {
  flex-shrink: 0;
  align-self: flex-end;
  margin-bottom: 2px;
  animation: qb-bob 3.2s ease-in-out infinite;
}
.qb-avatar-img {
  width: 24px; height: 24px;
  object-fit: contain;
  display: block;
  filter: drop-shadow(0 2px 5px rgba(99,102,241,.25));
}

/* Bubbles */
.qb-bubble {
  max-width: 76%;
  padding: 10px 14px;
  border-radius: 16px;
  font-size: .84rem;
  line-height: 1.55;
  white-space: pre-wrap;
  word-break: break-word;
}
.qb-msg--user .qb-bubble {
  background: linear-gradient(135deg, #667eea, #764ba2);
  color: #fff;
  border-bottom-right-radius: 4px;
}
.qb-msg--ai .qb-bubble {
  background: #fff;
  color: #1a202c;
  border: 1px solid #e2e8f0;
  border-bottom-left-radius: 4px;
  box-shadow: 0 1px 4px rgba(0,0,0,.06);
}

/* RTL */
.qb-bubble--rtl {
  direction: rtl;
  text-align: right;
  font-family: 'Segoe UI', Tahoma, 'Cairo', 'Noto Sans Arabic', Arial, sans-serif;
  line-height: 1.8;
}

/* Markdown */
.qb-bubble .qb-md-h {
  font-size: .88rem; font-weight: 800;
  margin: 10px 0 6px;
  padding-bottom: 4px;
  border-bottom: 1px solid rgba(0,0,0,.08);
}
.qb-bubble .qb-md-step {
  display: flex; align-items: flex-start; gap: 8px;
  margin: 5px 0; line-height: 1.55;
}
.qb-bubble .qb-md-num {
  min-width: 22px; height: 22px;
  border-radius: 50%;
  background: linear-gradient(135deg, #667eea, #764ba2);
  color: #fff; font-size: .68rem; font-weight: 800;
  display: flex; align-items: center; justify-content: center;
  flex-shrink: 0; margin-top: 2px;
}
.qb-msg--user .qb-bubble .qb-md-num {
  background: rgba(255,255,255,.25);
}
.qb-bubble .qb-md-bullet {
  display: flex; align-items: flex-start; gap: 7px;
  margin: 4px 0; line-height: 1.55;
}
.qb-bubble .qb-md-dot {
  font-size: .45rem; margin-top: 6px;
  flex-shrink: 0; opacity: .6;
}
.qb-bubble .qb-md-gap { height: 6px; }
.qb-bubble strong { font-weight: 700; }
.qb-bubble--rtl .qb-md-step,
.qb-bubble--rtl .qb-md-bullet { flex-direction: row-reverse; }

/* Typing indicator */
.qb-typing {
  display: flex; align-items: center; gap: 4px;
  padding: 12px 16px;
}
.qb-typing span {
  width: 7px; height: 7px;
  background: #a0aec0;
  border-radius: 50%;
  display: inline-block;
  animation: qb-bounce .9s ease-in-out infinite;
}
.qb-typing span:nth-child(2) { animation-delay: .18s; }
.qb-typing span:nth-child(3) { animation-delay: .36s; }
@keyframes qb-bounce {
  0%, 80%, 100% { transform: translateY(0); }
  40%           { transform: translateY(-6px); }
}

/* Input row */
.qb-input-row {
  display: flex; gap: 8px;
  padding: 12px 14px;
  border-top: 1px solid #e2e8f0;
  background: #fff;
  flex-shrink: 0;
}
.qb-input {
  flex: 1;
  border: 1.5px solid #e2e8f0;
  border-radius: 10px;
  padding: 9px 13px;
  font-size: .84rem;
  font-family: inherit;
  outline: none;
  transition: border-color .2s;
  background: #f8fafc;
  color: #1a202c;
  resize: none;
  overflow-y: auto;
  min-height: 38px;
  max-height: 120px;
  line-height: 1.5;
  display: block;
}
.qb-input:focus   { border-color: #667eea; background: #fff; }
.qb-input:disabled { opacity: .6; }
.qb-send {
  width: 38px; height: 38px;
  background: linear-gradient(135deg, #667eea, #764ba2);
  border: none; border-radius: 10px;
  cursor: pointer;
  display: flex; align-items: center; justify-content: center;
  flex-shrink: 0;
  transition: opacity .2s, transform .15s;
}
.qb-send svg { fill: #fff; }
.qb-send:hover:not(:disabled) { opacity: .88; transform: scale(1.05); }
.qb-send:disabled { opacity: .4; cursor: not-allowed; }

@media (max-width: 480px) {
  .qb-panel { width: calc(100vw - 48px); }
  .qb-widget { right: 12px; bottom: 12px; }
}
</style>
